import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  FormControlLabel,
  Switch,
  Avatar,
} from "@mui/material";
import { User } from "../../store/slices/types";

interface UserFormProps {
  user?: User | null;
  onSave: (userData: Partial<User>) => void;
  onCancel: () => void;
  saving?: boolean;
}

const UserForm: React.FC<UserFormProps> = ({
  user,
  onSave,
  onCancel,
  saving = false,
}) => {
  const [formData, setFormData] = useState({
    email: user?.email || "",
    username: user?.username || "",
    displayName: user?.displayName || user?.fullName || "",
    profilePhoto: user?.profilePhoto || "",
    preferences: {
      notifications: user?.preferences?.notifications !== undefined 
        ? user.preferences.notifications 
        : true,
      theme: user?.preferences?.theme || "light",
      language: user?.preferences?.language || "en",
      isActive: user?.preferences?.isActive !== undefined 
        ? user.preferences.isActive 
        : true,
    },
  });

  useEffect(() => {
    setFormData({
      email: user?.email || "",
      username: user?.username || "",
      displayName: user?.displayName || user?.fullName || "",
      profilePhoto: user?.profilePhoto || "",
      preferences: {
        notifications: user?.preferences?.notifications !== undefined 
          ? user.preferences.notifications 
          : true,
        theme: user?.preferences?.theme || "light",
        language: user?.preferences?.language || "en",
        isActive: user?.preferences?.isActive !== undefined 
          ? user.preferences.isActive 
          : true,
      },
    });
    setErrors({});
  }, [user]);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.username.trim()) {
      newErrors.username = "Username is required";
    } else if (formData.username.trim().length < 3) {
      newErrors.username = "Username must be at least 3 characters";
    }

    if (!formData.displayName.trim()) {
      newErrors.displayName = "Display name is required";
    } else if (formData.displayName.trim().length < 2) {
      newErrors.displayName = "Display name must be at least 2 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSave({
        email: formData.email,
        username: formData.username,
        displayName: formData.displayName,
        // profilePhoto is read-only - not included in save
        preferences: formData.preferences,
      });
    }
  };

  const displayName = formData.displayName || formData.username || formData.email || "User";
  const initials = displayName.charAt(0).toUpperCase();

  return (
    <Box>
      {/* Profile Photo Section - Read Only */}
      <Box sx={{ mb: 4, display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Box sx={{ position: "relative", mb: 2 }}>
          <Avatar
            src={formData.profilePhoto}
            alt={displayName}
            sx={{
              width: 120,
              height: 120,
              bgcolor: formData.profilePhoto ? "transparent" : "primary.main",
              fontSize: "3rem",
              border: "3px solid",
              borderColor: "primary.main",
            }}
          >
            {!formData.profilePhoto && initials}
          </Avatar>
        </Box>
        <Typography variant='body2' color='text.secondary' sx={{ textAlign: "center", maxWidth: 400 }}>
          Profile photo can only be updated from the mobile app
        </Typography>
      </Box>

      {/* Basic Information Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant='h6'
          fontWeight='600'
          gutterBottom
          sx={{
            color: "primary.main",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          📝 Basic Information
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Email'
              type='email'
              placeholder='user@example.com'
              value={formData.email}
              onChange={(e) => {
                setFormData({ ...formData, email: e.target.value });
                if (errors.email) setErrors({ ...errors, email: "" });
              }}
              error={!!errors.email}
              helperText={errors.email}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  minHeight: 64,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Username'
              placeholder='johndoe'
              value={formData.username}
              onChange={(e) => {
                setFormData({ ...formData, username: e.target.value });
                if (errors.username) setErrors({ ...errors, username: "" });
              }}
              error={!!errors.username}
              helperText={errors.username}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  minHeight: 64,
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label='Display Name'
              placeholder='John Doe'
              value={formData.displayName}
              onChange={(e) => {
                setFormData({ ...formData, displayName: e.target.value });
                if (errors.displayName)
                  setErrors({ ...errors, displayName: "" });
              }}
              error={!!errors.displayName}
              helperText={errors.displayName}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  minHeight: 64,
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Preferences Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant='h6'
          fontWeight='600'
          gutterBottom
          sx={{
            color: "primary.main",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          ⚙️ User Preferences
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.preferences.notifications}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      preferences: {
                        ...formData.preferences,
                        notifications: e.target.checked,
                      },
                    })
                  }
                />
              }
              label='Enable Notifications'
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.preferences.isActive}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      preferences: {
                        ...formData.preferences,
                        isActive: e.target.checked,
                      },
                    })
                  }
                />
              }
              label='Active User'
            />
          </Grid>
        </Grid>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}>
        <Button
          onClick={onCancel}
          variant='outlined'
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant='contained'
          disabled={saving}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: "none",
            fontWeight: 600,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
            },
          }}
        >
          {saving ? "Saving..." : user ? "Update User" : "Create User"}
        </Button>
      </Box>
    </Box>
  );
};

export default UserForm;

